import { useEffect, useId, useRef, useState } from 'react';
import { discoveryLocationApi } from '../../api/discoveryLocationApi';
import { journeyDiscoveryApi } from '../../api/journeyDiscoveryApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { SOCIAL_LOCATION_SEARCH_CHROME as searchChrome } from '../social-post/socialActionPresentation';
import {
  locationPathLabel,
  normalizeGeoLocationList,
  normalizeGeoLocationNode,
} from './socialComposeModel';

export const SOCIAL_LOCATION_SEARCH_MIN_CODE_POINTS = 2;
export const SOCIAL_LOCATION_SEARCH_DEBOUNCE_MS = 250;
export const SOCIAL_LOCATION_SEARCH_LIMIT = 8;

function geoSuggestionItems(payload) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  return items
    .filter((item) => item?.type === 'GEO' && /^[1-9]\d*$/.test(String(item?.id ?? '')))
    .map((item) => ({
      locationId: String(item.id),
      label: String(item.label || '').trim(),
      secondaryLabel: String(item.secondaryLabel || '').trim(),
    }))
    .filter((item) => item.label);
}

export default function SocialLocationSelector({
  selected,
  onSelect,
  disabled = false,
  rootLabel,
  searchable = false,
}) {
  const { t } = useTravelTranslation();
  const statusId = useId();
  const searchId = useId();
  const searchHintId = useId();
  const [ancestors, setAncestors] = useState([]);
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [searchItems, setSearchItems] = useState([]);
  const [searchPhase, setSearchPhase] = useState('idle');
  const [searchError, setSearchError] = useState('');
  const [resolvingId, setResolvingId] = useState('');
  const searchSeq = useRef(0);
  const resolveSeq = useRef(0);
  const resolvedRoot = rootLabel || t('travel.discover.locationSelector.root');

  useEffect(() => {
    if (!searchable) return undefined;
    const trimmed = query.trim();
    const seq = ++searchSeq.current;
    // 關鍵字改變時，進行中的地點確認一律作廢，避免選到畫面上已不存在的結果
    resolveSeq.current += 1;
    setResolvingId('');
    if (Array.from(trimmed).length < SOCIAL_LOCATION_SEARCH_MIN_CODE_POINTS) {
      setSearchItems([]);
      setSearchPhase('idle');
      setSearchError('');
      return undefined;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearchPhase('loading');
      setSearchError('');
      try {
        const payload = await journeyDiscoveryApi.suggestions(
          { q: trimmed, limit: SOCIAL_LOCATION_SEARCH_LIMIT },
          { signal: controller.signal },
        );
        if (seq !== searchSeq.current) return;
        setSearchItems(geoSuggestionItems(payload));
        setSearchPhase('ready');
      } catch (cause) {
        if (seq !== searchSeq.current || controller.signal.aborted) return;
        setSearchItems([]);
        setSearchError(searchChrome.failed);
        setSearchPhase('error');
      }
    }, SOCIAL_LOCATION_SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, searchable]);

  useEffect(() => () => {
    // 卸載後不得再寫入搜尋或選取結果
    searchSeq.current += 1;
    resolveSeq.current += 1;
  }, []);

  const searchStatus = searchPhase === 'loading'
    ? searchChrome.searching
    : (searchPhase === 'ready'
      ? (searchItems.length ? searchChrome.resultCount(searchItems.length) : searchChrome.empty)
      : '');

  async function selectSearchResult(item) {
    if (disabled || resolvingId) return;
    const seq = ++resolveSeq.current;
    setResolvingId(item.locationId);
    setSearchError('');
    try {
      // 以地點詳情再次確認仍為 ACTIVE 且可被目前使用者使用
      const node = normalizeGeoLocationNode(await discoveryLocationApi.getById(item.locationId));
      if (seq !== resolveSeq.current) return;
      if (!node) throw new Error('location unavailable');
      onSelect(node);
    } catch (cause) {
      if (seq !== resolveSeq.current) return;
      setSearchError(searchChrome.resolveFailed);
    } finally {
      if (seq === resolveSeq.current) setResolvingId('');
    }
  }

  const parentId = ancestors.length ? ancestors[ancestors.length - 1].locationId : '';

  async function load(parentLocationId) {
    setPhase('loading');
    setError('');
    try {
      const payload = await discoveryLocationApi.listByParent(parentLocationId || undefined);
      setItems(normalizeGeoLocationList(payload));
      setPhase('ready');
    } catch (cause) {
      setError(cause?.message || t('travel.discover.locationSelector.loadFailed'));
      setPhase('error');
    }
  }

  useEffect(() => {
    load(parentId);
    // Reload when the drill-down parent changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentId]);

  function openChild(node) {
    if (disabled || !node?.hasChildren) return;
    setAncestors((current) => [...current, node]);
  }

  function goBack() {
    if (disabled || !ancestors.length) return;
    setAncestors((current) => current.slice(0, -1));
  }

  return (
    <div className="social-compose__location" data-testid="social-location-selector">
      <div className="social-compose__location-toolbar">
        <p id={statusId} className="social-compose__location-path">
          {selected
            ? `${t('travel.discover.locationSelector.selectedPrefix')}${locationPathLabel(selected, [])}`
            : (ancestors.length ? locationPathLabel(ancestors[ancestors.length - 1], ancestors.slice(0, -1)) : resolvedRoot)}
        </p>
        {ancestors.length ? (
          <button type="button" onClick={goBack} disabled={disabled}>
            {t('travel.discover.locationSelector.back')}
          </button>
        ) : null}
        {selected ? (
          <button type="button" onClick={() => onSelect(null)} disabled={disabled}>
            {t('travel.discover.locationSelector.clear')}
          </button>
        ) : null}
      </div>
      {searchable ? (
        <div className="social-compose__location-search" data-testid="social-location-search">
          <label htmlFor={searchId}>{searchChrome.label}</label>
          <input
            id={searchId}
            type="search"
            value={query}
            placeholder={searchChrome.placeholder}
            onChange={(event) => setQuery(event.target.value)}
            disabled={disabled}
            autoComplete="off"
            aria-describedby={searchHintId}
          />
          <p id={searchHintId} className="social-compose__hint">{searchChrome.hint}</p>
          <p className="social-compose__hint" aria-live="polite" aria-atomic="true" data-testid="social-location-search-status">
            {searchStatus}
          </p>
          {searchError ? <p role="alert">{searchError}</p> : null}
          {searchItems.length ? (
            <ul className="social-compose__location-list" data-testid="social-location-search-results">
              {searchItems.map((item) => {
                const isSelected = selected?.locationId === item.locationId;
                return (
                  <li key={item.locationId}>
                    <span>
                      <strong>{item.label}</strong>
                      {item.secondaryLabel ? <small>{item.secondaryLabel}</small> : null}
                    </span>
                    <span className="social-compose__location-actions">
                      <button
                        type="button"
                        aria-pressed={isSelected}
                        aria-busy={resolvingId === item.locationId}
                        disabled={disabled || Boolean(resolvingId)}
                        onClick={() => selectSearchResult(item)}
                      >
                        {isSelected ? searchChrome.selected : searchChrome.use}
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
      {phase === 'loading' ? <p aria-live="polite">{t('travel.discover.locationSelector.loading')}</p> : null}
      {error ? (
        <p role="alert">
          {error}
          {' '}
          <button type="button" onClick={() => load(parentId)} disabled={disabled}>
            {t('travel.common.retry')}
          </button>
        </p>
      ) : null}
      <ul className="social-compose__location-list" aria-describedby={statusId}>
        {items.map((node) => {
          const path = locationPathLabel(node, ancestors);
          const isSelected = selected?.locationId === node.locationId;
          return (
            <li key={node.locationId}>
              <span>
                <strong>{node.displayName}</strong>
                <small>{path}{node.locationType ? ` · ${node.locationType}` : ''}</small>
              </span>
              <span className="social-compose__location-actions">
                <button
                  type="button"
                  aria-pressed={isSelected}
                  disabled={disabled}
                  onClick={() => onSelect(node)}
                >
                  {isSelected
                    ? t('travel.discover.locationSelector.selected')
                    : t('travel.discover.locationSelector.use')}
                </button>
                {node.hasChildren ? (
                  <button type="button" disabled={disabled} onClick={() => openChild(node)}>
                    {t('travel.discover.locationSelector.openChildren')}
                  </button>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
      {phase === 'ready' && items.length === 0 ? (
        <p>{t('travel.discover.locationSelector.empty')}</p>
      ) : null}
    </div>
  );
}
